import React, { useState } from "react";
import AppShell from "@/components/AppShell";
import { api, formatApiError } from "@/lib/api";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkle, BookmarkSimple, CloudSun, Heart } from "@phosphor-icons/react";
import { toast } from "sonner";

const OCCASIONS = [
  "Date night", "Office", "First interview", "Wedding guest", "Brunch",
  "Travel day", "Coffee catch-up", "Dinner party", "Gym/active", "Night out",
  "Casual errands", "Gallery / museum", "Holiday party",
];

const MOODS = ["Confident", "Soft & romantic", "Playful", "Edgy", "Minimal", "Cozy", "Powerful"];
const WEATHERS = ["Hot", "Warm", "Mild", "Chilly", "Cold", "Rainy"];

export default function Occasion() {
  const [occasion, setOccasion] = useState("Date night");
  const [customOccasion, setCustomOccasion] = useState("");
  const [mood, setMood] = useState("Confident");
  const [weather, setWeather] = useState("Mild");
  const [notes, setNotes] = useState("");
  const [outfit, setOutfit] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true); setOutfit(null);
    try {
      const { data } = await api.post("/outfits/generate", {
        occasion: customOccasion.trim() || occasion,
        mood, weather, notes,
      });
      setOutfit(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't generate an outfit");
    }
    setLoading(false);
  };

  const save = async () => {
    if (!outfit) return;
    try {
      await api.post(`/outfits/${outfit.id}/save`);
      toast.success("Saved to your look book");
    } catch { toast.error("Could not save"); }
  };

  return (
    <AppShell>
      <p className="text-[0.7rem] tracking-[0.3em] uppercase text-charcoal-500">Occasion mode</p>
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-3 text-charcoal-900 tracking-tight">Dress the moment.</h1>
      <p className="mt-3 max-w-2xl text-charcoal-600">Choose the event, the mood, the weather. Ava builds a full look tuned to your profile and your wardrobe.</p>

      <div className="mt-10 grid lg:grid-cols-5 gap-8">
        {/* Controls */}
        <div className="lg:col-span-2 rounded-3xl border border-cream-200 bg-cream-100 p-7">
          <div>
            <p className="text-[0.65rem] tracking-[0.3em] uppercase text-charcoal-500 mb-3">Occasion</p>
            <div className="flex flex-wrap gap-2">
              {OCCASIONS.map((o) => (
                <button
                  key={o}
                  onClick={() => { setOccasion(o); setCustomOccasion(""); }}
                  data-testid={`occasion-${o}`}
                  className={`rounded-full px-3 py-1.5 text-xs tracking-wide transition-all ${
                    (!customOccasion && occasion === o) ? "bg-charcoal-700 text-white" : "bg-cream-50 border border-cream-200 text-charcoal-700"
                  }`}
                >{o}</button>
              ))}
            </div>
            <input
              value={customOccasion} onChange={(e) => setCustomOccasion(e.target.value)}
              data-testid="custom-occasion"
              placeholder="Or type your own…"
              className="mt-3 w-full bg-transparent border-0 border-b-2 border-cream-200 focus:border-charcoal-700 focus:outline-none py-2 text-sm"
            />
          </div>

          <div className="mt-6">
            <p className="text-[0.65rem] tracking-[0.3em] uppercase text-charcoal-500 mb-3">Mood</p>
            <div className="flex flex-wrap gap-2">
              {MOODS.map((m) => (
                <button
                  key={m} onClick={() => setMood(m)}
                  data-testid={`mood-${m}`}
                  className={`rounded-full px-3 py-1.5 text-xs transition-all ${
                    mood === m ? "bg-charcoal-700 text-white" : "bg-cream-50 border border-cream-200 text-charcoal-700"
                  }`}
                >{m}</button>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <p className="text-[0.65rem] tracking-[0.3em] uppercase text-charcoal-500 mb-3 flex items-center gap-2"><CloudSun size={14} /> Weather</p>
            <div className="flex flex-wrap gap-2">
              {WEATHERS.map((w) => (
                <button
                  key={w} onClick={() => setWeather(w)}
                  data-testid={`weather-${w}`}
                  className={`rounded-full px-3 py-1.5 text-xs transition-all ${
                    weather === w ? "bg-charcoal-700 text-white" : "bg-cream-50 border border-cream-200 text-charcoal-700"
                  }`}
                >{w}</button>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <p className="text-[0.65rem] tracking-[0.3em] uppercase text-charcoal-500 mb-3">Anything else?</p>
            <textarea
              value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              data-testid="occasion-notes"
              placeholder="e.g. I want to feel tall and confident"
              className="w-full bg-cream-50 border border-cream-200 rounded-2xl p-3 text-sm focus:outline-none focus:border-charcoal-700"
            />
          </div>

          <button
            onClick={generate} disabled={loading}
            data-testid="generate-outfit-button"
            className="btn-primary-pill w-full mt-7 disabled:opacity-60"
          >
            {loading ? "Styling your look…" : <><Sparkle weight="fill" size={16} className="mr-2" /> Generate outfit</>}
          </button>
        </div>

        {/* Result */}
        <div className="lg:col-span-3">
          <AnimatePresence mode="wait">
            {loading && (
              <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="rounded-3xl border border-cream-200 bg-cream-100 h-full p-10 flex flex-col items-center justify-center min-h-[400px] text-center">
                <div className="flex gap-1 mb-5">
                  <span className="typing-dot"></span><span className="typing-dot"></span><span className="typing-dot"></span>
                </div>
                <p className="font-serif text-2xl italic text-charcoal-800">Ava is mixing and matching…</p>
              </motion.div>
            )}
            {!loading && outfit && (
              <motion.div key="outfit" initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
                className="rounded-3xl border border-cream-200 bg-cream-50 overflow-hidden" data-testid="outfit-result">
                <div className="p-7 sm:p-10">
                  <p className="text-[0.65rem] tracking-[0.3em] uppercase text-charcoal-500">Your look</p>
                  <h2 className="font-serif text-3xl sm:text-4xl mt-3 text-charcoal-900 tracking-tight">{outfit.title}</h2>
                  <div className="mt-8 grid sm:grid-cols-2 gap-x-10 gap-y-5">
                    <Line label="Top" value={outfit.top} />
                    <Line label="Bottom" value={outfit.bottom} />
                    <Line label="Shoes" value={outfit.shoes} />
                    <Line label="Accessories" value={outfit.accessories} />
                    <Line label="Hair" value={outfit.hair} />
                    <Line label="Makeup" value={outfit.makeup} />
                  </div>

                  <div className="mt-8 rounded-2xl border border-cream-200 bg-accent-butter p-5">
                    <p className="text-[0.65rem] tracking-[0.3em] uppercase text-charcoal-500 mb-2">Why it works</p>
                    <p className="text-charcoal-800 leading-relaxed">{outfit.why_it_works}</p>
                  </div>

                  <div className="mt-5 grid sm:grid-cols-2 gap-4">
                    <div className="rounded-2xl border border-cream-200 bg-accent-sage p-5">
                      <p className="text-[0.65rem] tracking-[0.3em] uppercase text-charcoal-500 mb-2">Bolder</p>
                      <p className="text-sm text-charcoal-800">{outfit.bolder_variation}</p>
                    </div>
                    <div className="rounded-2xl border border-cream-200 bg-accent-lavender p-5">
                      <p className="text-[0.65rem] tracking-[0.3em] uppercase text-charcoal-500 mb-2">Softer</p>
                      <p className="text-sm text-charcoal-800">{outfit.softer_variation}</p>
                    </div>
                  </div>

                  {outfit.confidence_note && (
                    <div className="mt-5 flex items-start gap-3 rounded-2xl border border-cream-200 bg-cream-100 p-5">
                      <Heart size={22} weight="thin" className="flex-shrink-0 mt-0.5" />
                      <p className="font-serif italic text-xl text-charcoal-800">"{outfit.confidence_note}"</p>
                    </div>
                  )}

                  <div className="mt-7 flex items-center gap-3">
                    <button onClick={save} data-testid="save-outfit-button" className="btn-outline-pill">
                      <BookmarkSimple size={16} className="mr-2" /> Save to look book
                    </button>
                    <button onClick={generate} data-testid="regenerate-outfit-button" className="btn-primary-pill">
                      <Sparkle size={16} className="mr-2" /> Try another
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
            {!loading && !outfit && (
              <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="rounded-3xl border border-dashed border-cream-200 bg-cream-50 p-12 min-h-[400px] flex flex-col items-center justify-center text-center">
                <Sparkle size={36} weight="thin" className="text-charcoal-700" />
                <h3 className="font-serif text-3xl mt-4">Ready when you are.</h3>
                <p className="text-charcoal-600 mt-3 max-w-sm">Pick your occasion, mood, and weather — Ava handles the rest.</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </AppShell>
  );
}

function Line({ label, value }) {
  return (
    <div>
      <p className="text-[0.6rem] tracking-[0.3em] uppercase text-charcoal-500">{label}</p>
      <p className="mt-1 text-charcoal-800 leading-relaxed">{value || "—"}</p>
    </div>
  );
}
